<template>
  <section class="point-settings" :aria-busy="busy">
    <h3>{{ memberId ? '회원별 적립 설정' : '사이트 공통 적립 설정' }}</h3>
    <p v-if="loading" role="status">설정을 불러오는 중입니다.</p>
    <p v-if="error" class="error" role="alert">{{ error }} <button type="button" @click="load" :disabled="busy">다시 불러오기</button></p>
    <p v-if="notice" class="notice" role="status">{{ notice }}</p>
    <fieldset v-for="row in rows" :key="row.category" :disabled="busy" class="point-row">
      <legend>{{ categoryName(row.category) }}</legend>
      <p v-if="memberId" class="help">사이트 공통: {{ enabledName(row.siteSetting?.enabled) }} · {{ rateName(row.siteSetting?.rate) }}</p>
      <p class="help">현재 적용: {{ enabledName(row.effective.enabled) }} · {{ rateName(row.effective.rate) }}<template v-if="memberId"> (사용 여부: {{ sourceName(row.effective.source?.enabled) }}, 적립률: {{ sourceName(row.effective.source?.rate) }})</template></p>
      <p v-if="row.effective.configured === false" class="help">적용할 설정이 없습니다.</p>
      <div class="fields">
        <label>적립 여부
          <select v-model="row.enabled">
            <option v-if="memberId" value="inherit">사이트 공통 사용</option>
            <option v-else value="inherit" disabled>선택하세요</option>
            <option value="true">사용</option>
            <option value="false">중지</option>
          </select>
        </label>
        <label v-if="memberId" class="inherit"><input type="checkbox" v-model="row.inheritRate" /> 적립률 사이트 공통 사용</label>
        <label>적립률 (%)
          <input v-model.trim="row.rate" type="text" inputmode="decimal" autocomplete="off" placeholder="0.01 ~ 5.00" :disabled="!!memberId && row.inheritRate" />
        </label>
      </div>
      <div class="actions">
        <button type="button" @click="save(row)">{{ categoryName(row.category) }} 설정 저장</button>
        <button v-if="memberId" type="button" @click="reset(row)">회원 예외 해제</button>
      </div>
    </fieldset>
  </section>
</template>

<script>
import { getBetPoint, saveBetPoint, getMemberBetPoint, saveMemberBetPoint, resetMemberBetPoint } from '@/api/setting'

const errors = { '9002': '입력 형식이나 적립률 범위를 확인해주세요.', '9004': '사이트 또는 회원을 찾을 수 없습니다.', '1000': '설정 권한이 없습니다.', TK999: '로그인 정보가 변경되었습니다. 다시 로그인해주세요.', '9999': '인증 또는 서버 오류가 발생했습니다.' }
const validRate = rate => /^(?:0\.(?:0[1-9]|[1-9]\d?)|[1-4](?:\.\d{1,2})?|5(?:\.0{1,2})?)$/.test(rate)

export default {
  name: 'BetPointSettings',
  props: { memberId: { type: String, default: '' } },
  data () { return { rows: [], loading: false, saving: false, error: '', notice: '' } },
  computed: { busy () { return this.loading || this.saving } },
  methods: {
    categoryName (category) { return category === 'casino' ? '카지노' : '슬롯' },
    enabledName (value) { return value === true ? '사용' : value === false ? '중지' : '미설정' },
    rateName (value) { return value == null ? '미설정' : value + '%' },
    sourceName (source) { return { SITE: '사이트 공통', MEMBER: '회원별 설정', UNSET: '미설정' }[source] || '미설정' },
    responseError (response) { return errors[response?.resultCode] || response?.resultMessage || '요청을 처리하지 못했습니다.' },
    failureMessage (error) {
      if ([401, 422].includes(error.response?.status) || error.message === 'No token') return '로그인이 만료되었습니다. 다시 로그인해주세요.'
      return error.response || error.code ? '서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.' : error.message || '요청을 처리하지 못했습니다.'
    },
    async load () {
      if (this.busy) return
      this.loading = true
      this.error = ''
      this.rows = []
      try {
        const response = await (this.memberId ? getMemberBetPoint({ memId: this.memberId }) : getBetPoint({}))
        if (response?.resultCode !== '0') throw new Error(this.responseError(response))
        const list = response.data?.list
        if (!Array.isArray(list)) throw new Error('설정 응답 형식을 확인하지 못했습니다.')
        this.rows = ['casino', 'slot'].map(category => {
          const item = list.find(entry => entry?.category === category)
          if (!item) throw new Error('응답에 ' + this.categoryName(category) + ' 설정이 없습니다.')
          const site = item.siteSetting || item.setting || null
          const setting = this.memberId ? item.memberSetting : site
          const effective = item.effective || (!this.memberId && site ? site : null)
          if (!effective) throw new Error('실제 적용 설정을 확인하지 못했습니다.')
          return { category, siteSetting: site, effective, enabled: setting?.enabled == null ? 'inherit' : String(setting.enabled), inheritRate: setting?.rate == null, rate: setting?.rate == null ? '' : String(setting.rate) }
        })
      } catch (error) {
        this.rows = []
        this.error = this.failureMessage(error)
      } finally { this.loading = false }
    },
    async save (row) {
      if (this.busy) return
      this.error = ''
      this.notice = ''
      const enabled = row.enabled === 'inherit' ? null : row.enabled === 'true'
      const rate = this.memberId && row.inheritRate ? null : row.rate
      if (!this.memberId && enabled === null) { this.error = '적립 여부를 선택해주세요.'; return }
      if (rate !== null && !validRate(rate)) { this.error = '적립률은 0.01~5.00 사이의 숫자로 소수점 둘째 자리까지 입력해주세요.'; return }
      const params = { category: row.category, enabled, rate }
      if (this.memberId) params.memId = this.memberId
      if (!confirm(this.categoryName(row.category) + ' 적립 설정을 저장하시겠습니까?')) return
      this.saving = true
      try {
        const response = await (this.memberId ? saveMemberBetPoint(params) : saveBetPoint(params))
        if (response?.resultCode !== '0') throw new Error(this.responseError(response))
        this.notice = '설정을 저장했습니다.'
      } catch (error) { this.error = this.failureMessage(error) } finally { this.saving = false }
      if (!this.error) await this.load()
    },
    async reset (row) {
      if (this.busy || !this.memberId || !confirm(this.categoryName(row.category) + ' 회원 예외를 해제하고 사이트 공통 설정을 사용하시겠습니까?')) return
      this.error = ''
      this.notice = ''
      this.saving = true
      try {
        const response = await resetMemberBetPoint({ memId: this.memberId, category: row.category })
        if (response?.resultCode !== '0') throw new Error(this.responseError(response))
        this.notice = '회원 예외를 해제했습니다.'
      } catch (error) { this.error = this.failureMessage(error) } finally { this.saving = false }
      if (!this.error) await this.load()
    }
  },
  created () { this.load() }
}
</script>

<style scoped>
.point-settings {margin:18px 0;padding:18px;background:#1f2937;border:1px solid #313a49;border-radius:5px;color:#d1d5db;}
h3 {font-size:16px;margin-bottom:10px;}.help {color:#cbd5e1;font-size:13px;line-height:1.6;margin:8px 0;}
.point-row {border:1px solid #475569;margin:16px 0;padding:14px;min-width:0;}.point-row legend {font-weight:700;padding:0 6px;}
.fields,.actions {display:flex;flex-wrap:wrap;align-items:center;gap:14px;margin-top:14px;}.fields label {display:flex;align-items:center;gap:8px;}
input:not([type=checkbox]),select {width:150px;padding:8px;border:1px solid #64748b;background:#111827;color:#fff;}input:disabled {opacity:.5;}
button {padding:8px 12px;background:#374151;color:#fff;border-radius:4px;cursor:pointer;font:inherit;}button:disabled,fieldset:disabled {opacity:.5;}
button:focus-visible,input:focus-visible,select:focus-visible {outline:2px solid #93c5fd;outline-offset:2px;}.error {color:#fca5a5;}.notice {color:#86efac;}
</style>
